import { useState } from "react";
import { Search, Package, Truck, CheckCircle2, XCircle } from "lucide-react";
import { api, apiError, formatINR } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

const ORDER_FLOW = ["placed", "confirmed", "shipped", "delivered"];
const STATUS_LABELS = { pending: "Payment pending", pay_on_delivery: "Pay on delivery", awaiting_verification: "Verifying payment", verified: "Payment verified", rejected: "Payment issue — we'll reach out" };

export default function TrackPage() {
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(ref.trim())}`, { params: { phone: phone.trim() } });
      setOrder(data);
    } catch (err) {
      setError(apiError(err, "We couldn't find that order."));
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? ORDER_FLOW.indexOf(order.order_status) : -1;

  return (
    <div className="mx-auto max-w-2xl px-4 py-14 sm:px-6" data-testid="track-page">
      <Seo title="Track Your Order — Anaya Fashion & Gifts" path="/track" noindex />
      <Reveal>
        <div className="text-center">
          <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Where&#8217;s my parcel?</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">Track your order</h1>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <form onSubmit={track} className="glass mt-8 rounded-3xl p-6 sm:p-8">
          <label className="block">
            <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Order reference</span>
            <input data-testid="track-ref-input" required value={ref} onChange={(e) => setRef(e.target.value)} placeholder="AN-XXXXXX"
              className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm uppercase outline-none focus:ring-1 focus:ring-primary" />
          </label>
          <label className="mt-4 block">
            <span className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Phone used at checkout</span>
            <input data-testid="track-phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile"
              className="mt-1.5 w-full rounded-xl border border-input bg-white/80 px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" />
          </label>
          {error && <p className="mt-4 rounded-xl bg-destructive/10 px-4 py-2.5 text-xs text-destructive" data-testid="track-error">{error}</p>}
          <button type="submit" data-testid="track-submit-btn" disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-foreground py-3.5 text-xs font-medium tracking-[0.2em] text-white uppercase transition-colors hover:bg-primary disabled:opacity-50">
            <Search size={14} /> {loading ? "Searching..." : "Track order"}
          </button>
        </form>
      </Reveal>

      {order && (
        <Reveal>
          <div className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid="track-result">
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl" data-testid="track-order-ref">{order.order_reference}</p>
              <span className="text-sm font-light text-muted-foreground">{formatINR(order.total)}</span>
            </div>
            {order.confirmation_code && (
              <p className="mt-1 text-xs tracking-[0.2em] text-primary uppercase" data-testid="track-confirmation-code">Confirmation code: {order.confirmation_code}</p>
            )}
            <p className="mt-1 text-xs tracking-[0.2em] text-muted-foreground uppercase" data-testid="track-payment-status">
              {STATUS_LABELS[order.payment_status] || order.payment_status}
            </p>
            {order.order_status === "cancelled" ? (
              <p className="mt-6 flex items-center gap-2 text-sm text-destructive"><XCircle size={16} /> This order was cancelled. Message us on WhatsApp if that seems wrong.</p>
            ) : (
              <div className="mt-8 flex items-center">
                {ORDER_FLOW.map((s, i) => (
                  <div key={s} className="flex flex-1 items-center last:flex-none">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${i <= currentStep ? "border-primary bg-primary text-white" : "border-input bg-white/70 text-muted-foreground"}`}>
                        {i === 0 ? <Package size={15} /> : i === ORDER_FLOW.length - 1 ? <CheckCircle2 size={15} /> : <Truck size={15} />}
                      </span>
                      <span className="text-[9px] tracking-[0.15em] uppercase">{s}</span>
                    </div>
                    {i < ORDER_FLOW.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < currentStep ? "bg-primary" : "bg-input"}`} />}
                  </div>
                ))}
              </div>
            )}
            <ul className="mt-8 space-y-2 border-t border-input pt-4 text-sm font-light">
              {order.items.map((i) => (
                <li key={i.product_id} className="flex justify-between gap-3">
                  <span className="line-clamp-1">{i.name}{i.variant ? ` · Design ${i.variant}` : ""} × {i.qty}</span>
                  <span className="shrink-0">{formatINR(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      )}
    </div>
  );
}
